<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import router from '@/router'

const { t } = useI18n()
const discordLink = ref<string>(import.meta.env.VITE_DISCORD_INVITE_LINK ?? '')

const route = useRoute()
const expandedItems = ref<Set<string>>(new Set())

// FAQ items configuration
const faqItems = ref([
	{
		id: 'what_are_tickets',
		icon: 'pi pi-ticket',
		iconColor: 'from-emerald-400 to-teal-500',
		accentColor: 'emerald',
	},
	{
		id: 'energy',
		icon: 'pi pi-bolt',
		iconColor: 'from-yellow-400 to-orange-500',
		accentColor: 'yellow',
		isPro: true,
		isBasic: true,
	},
	{
		id: 'copyright',
		icon: 'pi pi-shield',
		iconColor: 'from-red-400 to-pink-500',
		accentColor: 'red',
	},
	{
		id: 'make_images',
		icon: 'pi pi-image',
		iconColor: 'from-purple-400 to-indigo-500',
		accentColor: 'purple',
	},
	{
		id: 'eco_mode',
		icon: 'pi pi-clock',
		iconColor: 'from-blue-400 to-cyan-500',
		accentColor: 'blue',
		isPro: true,
	},
	{
		id: '600_img',
		icon: 'pi pi-calendar',
		iconColor: 'from-green-400 to-emerald-500',
		accentColor: 'green',
		isPro: true,
	},
])

watch(() => route.hash, () => {
	const mapping = [
		'what_are_tickets',
		'energy',
		'copyright',
		'make_images',
		'eco_mode',
		'600_img',
	]
	const hashId = mapping.find(hash => route.hash.includes(hash))
	if (hashId) {
		expandedItems.value.add(hashId)
		// Scroll to element with proper offset after DOM update
		setTimeout(() => {
			const element = document.getElementById(hashId)
			if (element) {
				const headerOffset = 100 // Adjust this value based on your header height
				const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
				const offsetPosition = elementPosition - headerOffset

				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth',
				})
			}
		}, 300) // Wait for expansion animation to complete
	}
}, { immediate: true })

function toggleItem(itemId: string) {
	if (expandedItems.value.has(itemId)) {
		expandedItems.value.delete(itemId)
	}
	else {
		expandedItems.value.add(itemId)
	}
}

function getAccentClasses(color: string) {
	const colorMap: Record<string, string> = {
		emerald: 'hover:border-emerald-400/50 group-hover:shadow-emerald-500/20',
		yellow: 'hover:border-yellow-400/50 group-hover:shadow-yellow-500/20',
		red: 'hover:border-red-400/50 group-hover:shadow-red-500/20',
		purple: 'hover:border-purple-400/50 group-hover:shadow-purple-500/20',
		blue: 'hover:border-blue-400/50 group-hover:shadow-blue-500/20',
		green: 'hover:border-green-400/50 group-hover:shadow-green-500/20',
	}
	return colorMap[color] || 'hover:border-blue-400/50 group-hover:shadow-blue-500/20'
}
</script>

<template>
	<div
		id="faq"
		class="flex flex-col gap-6 sm:gap-8 px-3 sm:px-4 w-full justify-center items-center py-6 sm:py-8 bg-slate-900 rounded-xl"
	>
		<!-- Header Section -->
		<div class="w-full flex flex-wrap justify-center">
			<div class="text-center mb-6 sm:mb-8">
				<h2 class="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-2">
					{{ t('faq.title') }}
				</h2>
				<div class="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto rounded-full" />
			</div>
			<div class="text-slate-300 font-medium text-xs sm:text-sm md:text-base flex justify-center items-center w-full px-2">
				<div class="w-fit text-center leading-relaxed">
					{{ t('faq.sub_title.part1') }}
					<a
						class="text-purple-400 font-semibold cursor-pointer select-none hover:text-purple-300 no-underline hover:underline underline-offset-2 transition-colors duration-200 break-words"
						:href="discordLink"
						target="_blank"
						aria-label="Discord ai-faplab.com community"
					>
						{{ t('faq.sub_title.discord_link') }}
					</a>
					{{ t('faq.sub_title.part2') }}
				</div>
			</div>
		</div>

		<!-- FAQ Cards Grid -->
		<div class="w-full max-w-4xl grid gap-3 sm:gap-4 md:gap-6">
			<div
				v-for="item in faqItems"
				:key="item.id"
				class="group cursor-pointer border-2 border-solid border-transparent rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg hover:shadow-2xl transition-all duration-500 relative overflow-hidden"
				:class="[
					getAccentClasses(item.accentColor),
					expandedItems.has(item.id) ? 'border-opacity-100' : '',
				]"
				@click="toggleItem(item.id)"
			>
				<!-- Background accents -->
				<div
					class="absolute -top-10 -right-10 w-24 sm:w-32 h-24 sm:h-32 rounded-full blur-xl opacity-20 transition-opacity duration-500 group-hover:opacity-30"
					:class="`bg-${item.accentColor}-500`"
				/>
				<div
					class="absolute -bottom-10 -left-10 w-24 sm:w-32 h-24 sm:h-32 rounded-full blur-xl opacity-10 transition-opacity duration-500 group-hover:opacity-20"
					:class="`bg-${item.accentColor}-400`"
				/>

				<!-- Card Header -->
				<div class="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 pb-3 sm:pb-4 relative z-10">
					<!-- Icon -->
					<div
						class="flex items-center justify-center w-10 sm:w-12 h-10 sm:h-12 rounded-xl shadow-lg flex-shrink-0 bg-gradient-to-br transition-transform duration-300 group-hover:scale-110"
						:class="item.iconColor"
					>
						<i
							:class="item.icon"
							class="text-white text-lg sm:text-xl"
						/>
					</div>

					<!-- Title -->
					<div class="flex-1 min-w-0">
						<h3
							:id="item.id"
							class="text-slate-100 font-bold text-base sm:text-lg md:text-xl flex flex-wrap items-center gap-2 scroll-mt-24 leading-tight"
						>
							<span class="break-words">
								{{ t(`faq.${item.id === '600_img' ? 'daily_tickets' : item.id}.title`) }}
							</span>
							<div class="flex gap-1 flex-wrap">
								<div
									v-if="item.isPro"
									class="pro-btn-component text-xs"
								>
									PRO
								</div>
								<div
									v-if="item.isBasic"
									class="basic-btn-component text-xs"
								>
									BASIC
								</div>
							</div>
						</h3>
					</div>

					<!-- Expand/Collapse Icon -->
					<div class="flex items-center justify-center w-7 sm:w-8 h-7 sm:h-8 rounded-full bg-slate-700/50 transition-all duration-300 flex-shrink-0">
						<i
							class="text-slate-300 transition-transform duration-300 text-sm sm:text-base"
							:class="expandedItems.has(item.id) ? 'pi pi-minus rotate-0' : 'pi pi-plus rotate-0'"
						/>
					</div>
				</div>

				<!-- Card Content -->
				<div
					class="overflow-hidden transition-all duration-500 ease-in-out relative z-10"
					:class="expandedItems.has(item.id) ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'"
				>
					<div class="px-4 sm:px-6 pb-4 sm:pb-6">
						<div class="border-t border-slate-700/50 pt-3 sm:pt-4">
							<!-- What are tickets content -->
							<div v-if="item.id === 'what_are_tickets'">
								<i18n-t
									scope="global"
									keypath="faq.what_are_tickets.message"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
									tag="p"
								>
									<template #getmore>
										<router-link
											class="text-purple-400 font-semibold hover:text-purple-300 underline underline-offset-2 break-words"
											to="/tickets"
										>
											{{ t('faq.what_are_tickets.get_more') }}
										</router-link>
									</template>
									<template #tickets_value>
										<span class="font-bold text-emerald-400 break-words">
											{{ t('faq.what_are_tickets.tickets_value') }}
										</span>
									</template>
								</i18n-t>
							</div>

							<!-- Energy content -->
							<div
								v-else-if="item.id === 'energy'"
								class="space-y-3"
							>
								<i18n-t
									scope="global"
									keypath="faq.energy.reset_info"
									tag="p"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
								>
									<template #basic>
										<span class="font-semibold text-yellow-400">{{ t('faq.energy.basic') }}</span>
									</template>
									<template #pro>
										<span class="font-semibold text-yellow-400">{{ t('faq.energy.pro') }}</span>
									</template>
									<template #reset_time>
										<span class="font-semibold text-yellow-400">{{ t('faq.energy.midnight_gmt') }}</span>
									</template>
								</i18n-t>

								<ul class="list-disc list-inside space-y-2 text-slate-300 ml-2 text-sm sm:text-base">
									<li class="break-words">
										<span class="font-semibold text-slate-100">
											{{ t('faq.energy.no_ticket_use') }}
										</span>
									</li>
									<li class="break-words">
										{{ t('faq.energy.usage') }}
									</li>
									<li class="break-words">
										{{ t('faq.energy.energy_value') }}
									</li>
								</ul>

								<i18n-t
									scope="global"
									keypath="faq.energy.after_depletion"
									tag="p"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
								>
									<template #eco_mode>
										<span class="font-semibold text-yellow-400">{{ t('generator.eco_mode') }}</span>
									</template>
								</i18n-t>
							</div>

							<!-- Copyright content -->
							<div v-else-if="item.id === 'copyright'">
								<i18n-t
									scope="global"
									keypath="faq.copyright.message"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
									tag="p"
								>
									<template #tos>
										<a
											class="text-red-400 font-semibold hover:text-red-300 underline underline-offset-2 break-words"
											href="/legal/TOS.en.pdf"
											target="_blank"
										>
											{{ t('legal.tos') }}
										</a>
									</template>
								</i18n-t>
							</div>

							<!-- Make images content -->
							<div v-else-if="item.id === 'make_images'">
								<i18n-t
									scope="global"
									keypath="faq.make_images.message"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
									tag="p"
								>
									<template #create_page>
										<router-link
											class="text-purple-400 font-semibold hover:text-purple-300 underline underline-offset-2 break-words"
											to="/create"
										>
											{{ t('faq.make_images.create_page') }}
										</router-link>
									</template>
									<template #the_guide>
										<router-link
											class="text-purple-400 font-semibold hover:text-purple-300 underline underline-offset-2 break-words"
											to="/guide"
										>
											{{ t('faq.make_images.the_guide') }}
										</router-link>
									</template>
								</i18n-t>
							</div>

							<!-- Eco mode content -->
							<div v-else-if="item.id === 'eco_mode'">
								<i18n-t
									scope="global"
									keypath="faq.eco_mode.message"
									tag="p"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
								>
									<template #eco_mode>
										<span class="font-semibold text-blue-400">{{ t('generator.eco_mode') }}</span>
									</template>
									<template #upscale>
										<span class="font-semibold text-blue-400">{{ t('faq.eco_mode.upscale') }}</span>
									</template>
									<template #free_features>
										<span class="font-semibold text-blue-400">{{ t('faq.eco_mode.free_features') }}</span>
									</template>
									<template #quota>
										<span class="font-semibold text-blue-400">{{ t('common.energy') }}</span>
									</template>
									<template #pro>
										<span class="font-semibold text-blue-400">{{ t('faq.eco_mode.pro') }}</span>
									</template>
									<template #settings>
										<span class="font-semibold text-blue-400">{{ t('faq.eco_mode.settings') }}</span>
									</template>
									<template #create_page>
										<span class="font-semibold text-blue-400">{{ t('faq.eco_mode.create_page') }}</span>
									</template>
								</i18n-t>
							</div>

							<!-- Daily tickets content -->
							<div
								v-else-if="item.id === '600_img'"
								class="space-y-3"
							>
								<i18n-t
									scope="global"
									keypath="faq.daily_tickets.p1"
									tag="p"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
								>
									<template #amount>
										<span class="font-semibold text-green-400">{{ t('faq.daily_tickets.amount') }}</span>
									</template>
									<template #reset_time>
										<span class="font-semibold text-green-400">{{ t('faq.daily_tickets.reset_time') }}</span>
									</template>
									<template #no_stack>
										<span class="font-semibold text-green-400">{{ t('faq.daily_tickets.no_stack') }}</span>
									</template>
								</i18n-t>

								<p class="leading-relaxed text-slate-300 text-sm sm:text-base break-words">
									{{ t('faq.daily_tickets.p2') }}
								</p>

								<ul class="list-disc list-inside space-y-2 text-slate-300 ml-2 text-sm sm:text-base">
									<li class="break-words">
										<i18n-t
											scope="global"
											keypath="faq.daily_tickets.list[0]"
										>
											<template #navbar>
												<span class="font-semibold text-slate-100">{{ t('faq.daily_tickets.navbar') }}</span>
											</template>
										</i18n-t>
									</li>
									<li class="break-words">
										<i18n-t
											scope="global"
											keypath="faq.daily_tickets.list[1]"
										>
											<template #create_button>
												<span class="font-semibold text-slate-100">{{ t('faq.daily_tickets.create_button') }}</span>
											</template>
										</i18n-t>
									</li>
									<li class="break-words">
										<i18n-t
											scope="global"
											keypath="faq.daily_tickets.list[2]"
										>
											<template #info_icon>
												<span class="font-semibold text-slate-100">{{ t('faq.daily_tickets.info_icon') }}</span>
											</template>
										</i18n-t>
									</li>
								</ul>

								<i18n-t
									scope="global"
									keypath="faq.daily_tickets.p3"
									tag="p"
									class="leading-relaxed text-slate-300 text-sm sm:text-base break-words"
								>
									<template #eco_mode>
										<span class="font-semibold text-green-400">{{ t('generator.eco_mode') }}</span>
									</template>
								</i18n-t>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
/* Smooth animations for expand/collapse */
.max-h-0 {
	max-height: 0;
}

.max-h-screen {
	max-height: 100vh;
}

/* Custom scrollbar for overflow content */
::-webkit-scrollbar {
	width: 4px;
}

::-webkit-scrollbar-track {
	background: rgba(51, 65, 85, 0.1);
}

::-webkit-scrollbar-thumb {
	background: rgba(148, 163, 184, 0.3);
	border-radius: 2px;
}

/* Ensure proper text wrapping */
.break-words {
	word-wrap: break-word;
	overflow-wrap: break-word;
	hyphens: auto;
}

/* Mobile specific adjustments */
@media (max-width: 640px) {
	.pro-btn-component,
	.basic-btn-component {
		font-size: 0.75rem;
		padding: 0.125rem 0.375rem;
	}
}
</style>
